import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Bot, Zap, Sparkles, User, ArrowRight, Trophy, Crown, Medal, Layers, School, Brain } from "lucide-react";
import SiteChrome from "@/components/SiteChrome";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { DEFAULT_AVATAR } from "@/lib/avatarBuilder";
import AvatarBuilder from "@/components/AvatarBuilder";
import PlayerProfileCard from "@/components/PlayerProfileCard";

/**
 * Public VS ROBOT landing — pick name + game + robot opponent.
 * No login required.
 */

// Memoized card so that typing in the "name" input doesn't re-render every
// game card in the grid. Only re-renders when its own `active`/`game`/`onPick`
// props change — and thanks to the useCallback on `onPick`, that's effectively
// only when the selection changes.
const GameCard = React.memo(function GameCard({ game, active, onPick, tiersLabel, levelsLabel }) {
  return (
    <button
      key={game.id}
      type="button"
      onClick={() => onPick(game)}
      className={`text-left p-4 rounded-2xl border-2 transition ${active ? "border-sky-500 bg-sky-50 shadow-md" : "border-brand-muted/30 bg-brand-bg2 hover:border-sky-300"}`}
      data-testid={`vsr-game-${game.id}`}
    >
      <div className="font-[Fredoka] font-bold text-brand-text">{game.name}</div>
      <div className="text-xs text-brand-muted mt-1">
        {game.supert_count ?? 1}× SUPERT · {game.level_system === "tiers" ? tiersLabel : levelsLabel}
      </div>
    </button>
  );
});

const RobotCard = React.memo(function RobotCard({ robot, active, onPick }) {
  return (
    <button
      key={robot.id}
      type="button"
      onClick={() => onPick(robot)}
      className={`text-left p-3 rounded-2xl border-2 transition flex items-start gap-3 ${active ? "border-sky-500 bg-sky-50 shadow-md" : "border-brand-muted/30 bg-brand-bg2 hover:border-sky-300"}`}
      data-testid={`vsr-robot-${robot.id}`}
    >
      <img src={robot.image_url} alt={robot.name} className="w-16 h-16 rounded-xl object-contain bg-brand-bg2 border border-brand-muted/30 shrink-0" loading="lazy" />
      <div className="flex-1 min-w-0">
        <div className="font-[Fredoka] font-bold text-brand-text truncate">{robot.name}</div>
        {robot.alcunha && <div className="text-xs text-brand-muted italic truncate">{robot.alcunha}</div>}
        <div className="flex gap-1 mt-1.5 flex-wrap">
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-bold">
            <Sparkles className="w-2.5 h-2.5" /> {robot.efficacy}%
          </span>
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 text-[10px] font-bold">
            <Zap className="w-2.5 h-2.5" /> {robot.speed_min}-{robot.speed_max}s
          </span>
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[10px] font-bold" title="Força mental — define os pontos no ranking">
            <Brain className="w-2.5 h-2.5" /> {robot.mental_strength ?? 5}/10
          </span>
        </div>
      </div>
    </button>
  );
});

export default function VsRobot() {
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const [name, setName] = useState(sessionStorage.getItem("vs_robot_name") || sessionStorage.getItem("player_name") || "");
  const [school, setSchool] = useState(sessionStorage.getItem("vs_robot_school") || sessionStorage.getItem("player_school") || "");
  const [nationality, setNationality] = useState(sessionStorage.getItem("player_nationality") || "PT");
  const [avatarCfg, setAvatarCfg] = useState(() => {
    try {
      const raw = sessionStorage.getItem("player_avatar_cfg");
      if (raw) return { ...DEFAULT_AVATAR, ...JSON.parse(raw) };
    } catch (_) { /* noop */ }
    return { ...DEFAULT_AVATAR };
  });
  const [editingProfile, setEditingProfile] = useState(
    !(sessionStorage.getItem("player_name") || sessionStorage.getItem("vs_robot_name"))
  );
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [games, setGames] = useState([]);
  const [robots, setRobots] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(1); // numeric level 1..5
  const [selectedTierId, setSelectedTierId] = useState(""); // for tier-system games
  const [selectedRobot, setSelectedRobot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState({ rows: [], title: "TOP semanal Vs Robot", subtitle: "" });

  // Warm gameplay letter assets while the player browses robots so the first
  // duel renders its cards instantly.
  // Pre-rasterization of gameplay assets (letter PNGs with outline filter)
  // is deliberately NOT done on this setup screen — it competes with input
  // handling. The GamePlay screen has its own warmup that runs after the
  // "READY" modal appears (see `GamePlay.jsx`), which is the correct time.

  useEffect(() => {
    (async () => {
      try {
        const [g, r, lb] = await Promise.all([
          api.get(`/games?active_only=true&status=training&lang=${encodeURIComponent(lang || "pt")}`),
          api.get(`/robots/public?lang=${lang}`),
          api.get(`/vs-robot/leaderboard?lang=${lang}`),
        ]);
        setGames((g.data || []).filter((x) => x.active !== false && (x.status || "competition") === "training"));
        setRobots(r.data || []);
        // Backend now returns {title, subtitle, public_limit, season_start_at, rows}.
        setLeaderboard(lb.data || { rows: [] });
      } catch (e) {
        toast.error(t("vsr_load_failed"));
      } finally {
        setLoading(false);
      }
    })();
  }, [lang]);

  // Robots available for the selected game AND level (multi-game/multi-level support).
  // Memoized so it only recomputes when the actual inputs change — otherwise
  // every keystroke on the name/school inputs would re-filter the whole array.
  const availableRobots = useMemo(() => {
    if (!selectedGame) return [];
    return robots.filter((r) => {
      const gids = r.game_ids || [];
      const gameOk = gids.length > 0
        ? gids.includes(selectedGame.id)
        : (!r.game_id || r.game_id === selectedGame.id);
      if (!gameOk) return false;
      const lvls = r.levels || [];
      return lvls.length === 0 || lvls.includes(parseInt(selectedLevel, 10));
    });
  }, [selectedGame, selectedLevel, robots]);

  // Stable click handlers so memoized cards don't invalidate on every keystroke.
  const pickGame = useCallback((g) => setSelectedGame(g), []);
  const pickRobot = useCallback((r) => setSelectedRobot(r), []);

  // When the selected game changes, reset the level/tier defaults.
  useEffect(() => {
    if (!selectedGame) return;
    if (selectedGame.level_system === "tiers" && Array.isArray(selectedGame.tiers) && selectedGame.tiers.length > 0) {
      const first = selectedGame.tiers[0];
      setSelectedTierId(first.id);
      setSelectedLevel(parseInt(first.level, 10) || 1);
    } else {
      setSelectedTierId("");
      setSelectedLevel(1);
    }
  }, [selectedGame]);

  // Auto-deselect robot ONLY when the game changes to one that doesn't
  // include the currently-picked robot. Uses `game_ids` (array) and falls
  // back to the legacy `game_id` (single) so it works with both schemas.
  // Deliberately NOT keyed on `availableRobots` / `selectedLevel` — the
  // level can flip during a spurious re-render, and the previous version
  // wrongly nulled the selection in that case (production bug, Feb 2026).
  useEffect(() => {
    if (!selectedRobot || !selectedGame) return;
    const gids = selectedRobot.game_ids || [];
    const belongsToGame = gids.length > 0
      ? gids.includes(selectedGame.id)
      : (!selectedRobot.game_id || selectedRobot.game_id === selectedGame.id);
    if (!belongsToGame) setSelectedRobot(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGame?.id]);

  // Validate required fields. Returns a list of human-readable messages for
  // every field that's still empty. The action handler shows ALL of them in
  // a single toast (rather than stopping at the first one) and gently
  // scrolls the page to the first missing field — much friendlier than a
  // greyed-out disabled button that gives no feedback at all.
  const validateRequired = () => {
    const missing = [];
    if (!name.trim()) missing.push({ msg: t("train_name_required"), anchor: "vsr-step-name" });
    if (!selectedGame) missing.push({ msg: t("train_game_required"), anchor: "vsr-step-game" });
    if (!selectedRobot) missing.push({ msg: t("vsr_pick_robot"), anchor: "vsr-step-robot" });
    return missing;
  };

  const startDuel = () => {
    const missing = validateRequired();
    if (missing.length > 0) {
      toast.error(
        missing.length === 1
          ? missing[0].msg
          : t("vsr_missing_fields", { n: missing.length, list: missing.map((m) => m.msg.replace(/\.$/, "")).join(" · ") }),
      );
      // Scroll to the first missing field so the user knows where to look.
      const el = document.getElementById(missing[0].anchor);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    sessionStorage.setItem("vs_robot_name", name.trim());
    sessionStorage.setItem("vs_robot_id", selectedRobot.id);
    sessionStorage.setItem("vs_robot_school", school.trim());
    // Reuse the GamePlay engine — persist the player profile the same way
    // /treinar does so GamePlay picks it up via sessionStorage.
    sessionStorage.setItem("player_name", name.trim());
    sessionStorage.setItem("player_school", school.trim());
    sessionStorage.setItem("player_nationality", nationality || "PT");
    sessionStorage.setItem("player_level", String(selectedLevel));
    if (selectedTierId) {
      sessionStorage.setItem("player_tier_id", selectedTierId);
    } else {
      sessionStorage.removeItem("player_tier_id");
    }
    nav(`/jogar/${selectedGame.id}?mode=vs_robot&robot=${selectedRobot.id}`);
  };

  return (
    <SiteChrome>
      <section className="max-w-6xl mx-auto px-6 pt-10 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-500/10 text-sky-700 text-xs font-bold tracking-[0.2em] uppercase mb-4">
            <Bot className="w-4 h-4" /> {t("mode_vs_robot")}
          </div>
          <h1 className="text-4xl md:text-6xl font-[Fredoka] font-semibold text-brand-text leading-tight">
            {t("vsr_hero_title_left")} <span className="text-sky-500">Robot</span>{t("vsr_hero_title_right")}
          </h1>
          <p className="mt-3 text-base md:text-lg text-brand-muted max-w-2xl mx-auto whitespace-pre-line" data-testid="vsr-hero-desc">
            {leaderboard?.intro || t("vsr_hero_desc")}
          </p>
        </motion.div>

        {/* Step 1: Profile (unified across modes) */}
        <PlayerProfileCard
          title={t("mode_vs_robot")}
          editing={editingProfile}
          setEditing={setEditingProfile}
          name={name}
          setName={setName}
          school={school}
          setSchool={setSchool}
          nationality={nationality}
          setNationality={setNationality}
          avatarCfg={avatarCfg}
          onPickAvatar={() => setShowAvatarPicker(true)}
          testIdPrefix="vsr-profile"
        />

        {/* Step 2: Game */}
        <Step number={2} title={t("vsr_step_game")} anchorId="vsr-step-game">
          {loading ? <div className="text-brand-muted text-sm">{t("vsr_loading")}</div> : games.length === 0 ? (
            <div className="text-brand-muted text-sm">{t("vsr_no_games")}</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {games.map((g) => (
                <GameCard
                  key={g.id}
                  game={g}
                  active={selectedGame?.id === g.id}
                  onPick={pickGame}
                  tiersLabel={t("tiers_label")}
                  levelsLabel={t("levels_label")}
                />
              ))}
            </div>
          )}
        </Step>

        {/* Step 3: Level/Tier */}
        <Step number={3} title={t("vsr_step_level")} hint={!selectedGame ? t("vsr_pick_game_first") : null}>
          {!selectedGame ? null : (
            <div className="max-w-md">
              <Label className="flex items-center gap-2 mb-1.5">
                <Layers className="w-4 h-4 text-brand-muted" />
                {selectedGame.level_system === "tiers" ? t("col_level_or_tier") : t("col_level")}
              </Label>
              {selectedGame.level_system === "tiers" && Array.isArray(selectedGame.tiers) && selectedGame.tiers.length > 0 ? (
                <Select
                  value={selectedTierId}
                  onValueChange={(id) => {
                    const tr = selectedGame.tiers.find((x) => x.id === id);
                    if (tr) { setSelectedTierId(id); setSelectedLevel(parseInt(tr.level, 10) || 1); }
                  }}
                >
                  <SelectTrigger data-testid="vsr-level"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {selectedGame.tiers.map((tier) => (
                      <SelectItem key={tier.id} value={tier.id} data-testid={`vsr-tier-${tier.id}`}>
                        {(tier.translations?.[lang]?.name) || tier.name} <span className="text-brand-muted text-xs">({t("level_paren")} {tier.level})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Select value={String(selectedLevel)} onValueChange={(v) => setSelectedLevel(parseInt(v, 10))}>
                  <SelectTrigger data-testid="vsr-level"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(selectedGame.format === "quiz" ? [1, 2, 3, 4] : [1, 2, 3, 4, 5]).map((n) => (
                      <SelectItem key={n} value={String(n)} data-testid={`vsr-level-${n}`}>{t("level_paren")} {n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </Step>

        {/* Step 4: Robot */}
        <Step number={4} title={t("vsr_step_robot")} hint={!selectedGame ? t("vsr_pick_game_first") : null} anchorId="vsr-step-robot">
          {!selectedGame ? null : availableRobots.length === 0 ? (
            <div className="rounded-xl bg-amber-50 border-2 border-amber-200 p-4 text-amber-900 text-sm" data-testid="vsr-no-robots">
              {t("vsr_no_robots")}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {availableRobots.map((r) => (
                <RobotCard
                  key={r.id}
                  robot={r}
                  active={selectedRobot?.id === r.id}
                  onPick={pickRobot}
                />
              ))}
            </div>
          )}
        </Step>

        {/* Action */}
        <div className="mt-10 text-center">
          <Button
            onClick={startDuel}
            className="bg-sky-500 hover:bg-sky-600 rounded-full font-bold h-12 px-8 text-base shadow-lg"
            data-testid="vsr-start"
          >
            <Trophy className="w-5 h-5 mr-2" /> {t("vsr_start_duel")}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <div className="mt-3">
            <Link to="/" className="text-sm text-brand-muted hover:text-sky-700 underline" data-testid="vsr-back-home">← {t("back_to_home")}</Link>
          </div>
        </div>

        {/* Leaderboard */}
        <Leaderboard data={leaderboard} />
      </section>
      {showAvatarPicker && (
        <AvatarBuilder
          current={avatarCfg}
          onSave={(cfg) => {
            setAvatarCfg(cfg);
            try { sessionStorage.setItem("player_avatar_cfg", JSON.stringify(cfg)); } catch (_) { /* noop */ }
            setShowAvatarPicker(false);
          }}
          onClose={() => setShowAvatarPicker(false)}
        />
      )}
    </SiteChrome>
  );
}

function Leaderboard({ data }) {
  const rows = Array.isArray(data?.rows) ? data.rows : (Array.isArray(data) ? data : []);
  const title = data?.title || "TOP semanal Vs Robot";
  const subtitle = data?.subtitle || "Os melhores caçadores de robots desta semana";
  if (rows.length === 0) {
    return (
      <div className="mt-12 rounded-3xl bg-brand-bg2 shadow-sm border-2 border-brand-bg2 p-6 text-center" data-testid="vsr-leaderboard-empty">
        <Crown className="w-8 h-8 text-amber-400 mx-auto mb-2" />
        <h3 className="text-lg font-[Fredoka] font-bold text-brand-text">{title}</h3>
        <p className="text-sm text-brand-muted mt-1">{subtitle}</p>
        <p className="text-xs text-brand-muted mt-2">Sê o primeiro a jogar esta semana e a aparecer aqui!</p>
      </div>
    );
  }
  return (
    <div className="mt-12 rounded-3xl bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 shadow-md border-2 border-amber-200 p-6" data-testid="vsr-leaderboard">
      <div className="flex items-center gap-3 mb-1">
        <Crown className="w-7 h-7 text-amber-500" />
        <h2 className="text-2xl font-[Fredoka] font-bold text-brand-text" data-testid="vsr-leaderboard-title">{title}</h2>
      </div>
      {subtitle && <p className="text-xs text-brand-muted mb-4 pl-10">{subtitle}</p>}
      <ol className="space-y-1.5">
        {rows.map((row, i) => {
          const rank = i + 1;
          const isPodium = rank <= 3;
          const medalColor =
            rank === 1 ? "text-amber-500" :
            rank === 2 ? "text-brand-muted" :
            rank === 3 ? "text-orange-400" : "text-slate-300";
          const pts = Number(row.points || 0);
          return (
            <li
              key={row.player_name}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${isPodium ? "bg-brand-bg2 border-amber-200 shadow-sm" : "bg-brand-bg2/60 border-brand-muted/30"}`}
              data-testid={`vsr-leaderboard-row-${rank}`}
            >
              <div className="w-8 flex justify-center">
                {isPodium ? <Medal className={`w-5 h-5 ${medalColor}`} /> : <span className="text-sm font-bold text-brand-muted">#{rank}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-[Fredoka] font-bold text-brand-text truncate" data-testid={`vsr-leaderboard-name-${rank}`}>{row.player_name}</div>
                <div className="text-[11px] text-brand-muted mt-0.5 flex items-center gap-2 flex-wrap">
                  {row.school && (
                    <span className="inline-flex items-center gap-1" data-testid={`vsr-leaderboard-school-${rank}`}>
                      <School className="w-3 h-3" /> {row.school}
                    </span>
                  )}
                  <span>{row.total} {row.total === 1 ? "duelo" : "duelos"}</span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-0.5 shrink-0">
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-extrabold font-[Fredoka] ${pts >= 0 ? "bg-violet-500 text-white" : "bg-rose-500 text-white"}`}
                  data-testid={`vsr-leaderboard-points-${rank}`}
                  title="Pontos = soma de (vitórias × 2 × força mental) − (derrotas × 0.5 × força mental)"
                >
                  <Brain className="w-3 h-3" /> {pts >= 0 ? "+" : ""}{Math.round(pts * 10) / 10} pts
                </span>
                <div className="flex gap-1 text-[10px] font-bold">
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-100 text-green-700" data-testid={`vsr-leaderboard-wins-${rank}`}>
                    <Trophy className="w-2.5 h-2.5" /> {row.wins}
                  </span>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700">
                    ✗ {row.losses}
                  </span>
                  {row.ties > 0 && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-brand-bg2 text-brand-muted">
                      = {row.ties}
                    </span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function Step({ number, title, hint, children, anchorId }) {
  return (
    <div id={anchorId} className="rounded-3xl bg-brand-bg2 shadow-sm border-2 border-brand-bg2 p-6 mb-4 scroll-mt-24">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-full bg-sky-500 text-white font-bold grid place-items-center font-[Fredoka]">{number}</div>
        <h2 className="text-xl font-[Fredoka] font-bold text-brand-text">{title}</h2>
        {hint && <span className="text-xs text-amber-700 italic">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
